import { useEffect, useRef, useState } from 'react'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import styles from './styles'
import { Helmet } from 'react-helmet'
import { handleEditorReady } from '../../utils'
import {
  InlineEditor,
  Bold,
  Italic,
  Essentials,
  Heading,
  CodeBlock,
  List,
  Link,
  Paragraph,
  AccessibilityHelp,
  Autoformat,
  AutoImage,
  BlockQuote,
  RemoveFormat,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  HorizontalLine,
  ListProperties,
  PageBreak,
  PictureEditing,
  SelectAll,
  Subscript,
  Superscript,
  Strikethrough,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  Undo,
  GeneralHtmlSupport,
  Alignment,
  UpcastWriter,
  Font,
  HtmlEmbed,
  Autosave,
} from 'ckeditor5'
import 'ckeditor5/ckeditor5.css'

import ENV from 'src/components/editor/config/environment'
import ImageGalleryPlugin from '../custom-plugins/image-gallery'
import VideoGalleryPlugin from '../custom-plugins/video-gallery'
import SimpleBoxToolbar from '../custom-plugins/simple-box-wrapper-options'
import ButtonOptionsPlugin from '../custom-plugins/actions'
import CustomDropdown from '../custom-plugins/ai-asistant'
import { useDispatch } from 'react-redux'
import SimpleBox from '../custom-plugins/simple-box'
import { withStyles } from '@material-ui/core'
import Embed from '../../components/embed'
import CommunityBoardModal from '../../components/community-board-modal'
import CustomVideo from '../custom-plugins/custom-video'
import {
  handleHeaderSettingsModal,
  replacedropAreaWideContent,
} from '../custom-plugins/simple-box/utils'
import InteractionBuilder from '../custom-plugins/interaction-builder'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import TextToSpeechPlugin from '../custom-plugins/text-to-speech'
import CommunityBlock from '@minicoursegenerator/community-block'
import CommunityBlockPlugin from '../custom-plugins/community-block-plugin'
import PixelQuestPlugin from '../custom-plugins/pixel-quest-plugin'
import CrosswordPuzzlePlugin from '../custom-plugins/crossword-puzzle-plugin'
import RiddlePlugin from '../custom-plugins/riddle-plugin'
import WordlePlugin from '../custom-plugins/wordle-plugin'
import JumblePlugin from '../custom-plugins/jumble-plugin'
import WordSearchPlugin from '../custom-plugins/word-search-plugin'
import ScormPlugin from '../custom-plugins/scorm-plugin'
import CustomDropMarkerPositioning from '../custom-plugins/custom-drop-maker-positioning'
import ChatInterface from '../interaction-builder/chat-interface'
import { getCollectionList } from 'src/reducers/collection/action'
import { getCollectionSettingsAction } from 'src/reducers/collection/action'
import { editorEnum } from 'src/model/editor/editorEnum'
import UserType from 'src/model/user-types'
import {
  resolvePrimaryColor,
  useWorkspaceBranding,
} from 'src/utils/workspace-logo'
import { getPortalPageEditing } from 'src/utils/portal-page-editing'
/**
 * A shop landing page (Catalog, About…) opened in this editor shows its own
 * name and description in the header, not the showcase collection's.
 */
const headerCollectionOf = (collection, landingPageData) => {
  const page = getPortalPageEditing()
  return page
    ? {
        ...collection,
        name: page.name,
        collectionName: page.name,
        description: landingPageData?.description || '',
      }
    : collection
}

// Plans without the link feature must not be able to smuggle a link in through
// the clipboard: disabling the `link` command only guards the toolbar, the paste
// pipeline still carries <a> elements straight into the model.
const unwrapAnchors = (writer, parent) => {
  for (const child of Array.from(parent.getChildren())) {
    if (!child.is || !child.is('element')) continue

    // Depth first, so nested anchors are handled before their parent is unwrapped.
    unwrapAnchors(writer, child)

    if (child.name === 'a') {
      // Keep the pasted text, drop the link itself.
      writer.unwrapElement(child)
    } else if (child.hasAttribute && child.hasAttribute('href')) {
      writer.removeAttribute('href', child)
    }
  }
}

const stripPastedLinks = editor => {
  if (!editor.plugins.has('ClipboardPipeline')) return

  editor.plugins.get('ClipboardPipeline').on(
    'inputTransformation',
    (event, data) => {
      if (!data.content) return
      unwrapAnchors(
        new UpcastWriter(editor.editing.view.document),
        data.content,
      )
    },
    { priority: 'high' },
  )
}

/**
 * The editor's own rendering of its content, cleaned of the artefacts we never want stored.
 *
 * Both sides of the autosave comparison go through this, and they have to: CKEditor re-serialises
 * whatever it is handed — plugins write their default attributes on load — so getData() after a
 * plain page open rarely matches the stored HTML byte for byte.
 */
const normalizeEditorData = data =>
  (data || '')
    .replace(/<p class="empty-dom">&nbsp;<\/p>/g, '')
    .replace(
      /<div class="video-wrapper[\s\S]*?<\/div>\s*<p>&nbsp;<\/p>/g,
      match => match.replace(/<p>&nbsp;<\/p>/g, ''),
    )
    .trim()

const CKEditorComponent = props => {
  const {
    classes,
    editorRef,
    onDropClose,
    saveData,
    initialData,
    doubleClickData,
    setDoubleClickData,
    fontSize,
    fontFamily,
    setIsOpen,
    language,
    languageSettings,
    isReadOnly,
    theme,
    cardId,
  } = props

  // Existing state
  const [isLayoutReady, setIsLayoutReady] = useState(false)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [localCollectionData, setLocalCollectionData] = useState(null)
  const [previousData, setPreviousData] = useState('')

  // Collection tracking state
  const [editorCollections, setEditorCollections] = useState('')

  // Chat Modal state
  const [isChatModalOpen, setIsChatModalOpen] = useState(false)
  const [chatModalData, setChatModalData] = useState(null)
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  // Existing refs
  const editorPresenceRef = useRef(null)
  const editorAnnotationsRef = useRef(null)
  const editorRevisionHistoryRef = useRef(null)
  const editorRevisionHistoryEditorRef = useRef(null)
  const editorRevisionHistorySidebarRef = useRef(null)
  const editorContainerRef = useRef(null)
  // Read by the SCORM plugin when a block is removed; a ref so the editor config built once stays current
  const cardIdRef = useRef(cardId)
  cardIdRef.current = cardId
  // Replaced in onReady by the editor's own rendering; normalised here too so the two are
  // comparable if an autosave somehow lands first.
  const previousDataRef = useRef(normalizeEditorData(initialData))

  // Existing selectors and params
  const { id, page, subPage } = useParams()
  const open = useSelector(state => state.course.sectionDrawerOpen)
  const courseCards = useSelector(state => state.course.courseCards)
  const course = useSelector(state => state.course.course)
  const shopClips = useSelector(state => state.course.shopClips) || []
  const pageUrl = window.location.pathname.split('/')[1]
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  const { value } = useCourseSettings('theme')

  const landingPageData =
    pageUrl === 'mini-course' ? courseSettings || {} : collectionSettings || {}
  // A page on the default color shows the workspace branding's (as the
  // preview does); re-rendered once the branding loads.
  useWorkspaceBranding()
  const pageAccent = resolvePrimaryColor(landingPageData.primaryColor)

  const collection = useSelector(state => state.collection)
  const collectionList = useSelector(state => state.collection.list)
  const dispatch = useDispatch()

  // Get user type to disable link feature for basic users
  const userControl = UserType[window.CONSTANTS.USER_ROLE_ID]
  // Basic plus every trial-equivalent plan (Trial, Standard) has no link feature.
  const isLinkRestrictedUser =
    userControl === 'Basic' || UserType.isTrialEquivalent(userControl)

  // Fetch collections when editorCollections changes
  useEffect(() => {
    if (editorCollections) {
      const collectionIdsArray = editorCollections
        .split(',')
        .map(id => id.trim())
        .filter(id => id !== '')

      let search = ''
      let sort = 'created_date_desc'
      let sortingColumn = ''
      let asc = false

      if (sort.includes('created_date')) {
        sortingColumn = 'createdAt'
        asc = sort === 'created_date_asc'
      } else if (sort.includes('name')) {
        sortingColumn = 'collectionName'
        asc = sort === 'name_asc'
      }

      const query = {
        filterText: search,
        currentPage: 0,
        pageSize: 100,
        sortingColumn: sortingColumn,
        asc: asc,
        collectionIds: collectionIdsArray,
      }

      dispatch(getCollectionList(query))
    }
  }, [editorCollections, dispatch])

  useEffect(() => {
    if (pageUrl === 'collection' && collection) {
      dispatch(getCollectionSettingsAction(collection.collection.id))
    }
  }, [pageUrl, collection.collection])

  const collectionGeneralSettings = useSelector(
    state => state.collection.collectionSettings,
  )
  const certificate = useSelector(state => state.certificate?.certificate)
  const paymentSetting = useSelector(state => state.certificate?.paymentSetting)
  const certificatePayment =
    pageUrl === 'mini-course'
      ? null
      : {
          isConfigured:
            !!certificate?.isActive &&
            !!paymentSetting?.isPaymentConfigured &&
            (paymentSetting?.amount ?? 0) > 0,
          amount: paymentSetting?.amount,
          currency: paymentSetting?.currency,
          priceType: paymentSetting?.priceType,
          priceMetadata: paymentSetting?.priceMetadata,
        }

  // Chat Modal Handlers
  const handleOpenChatModal = data => {
    setChatModalData(data)
    setIsChatModalOpen(true)

    if (data.mode === 'editInteraction' && data.existingHtml) {
      setIsInitialLoading(false)
    } else {
      setIsInitialLoading(true)
    }
    const modalPosition = data.position || {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    }
    setPosition(modalPosition)
  }

  const handleCloseChatModal = () => {
    setIsChatModalOpen(false)
    setChatModalData(null)
    setIsInitialLoading(false)
  }

  const handleStreamResponse = response => {
    setIsInitialLoading(false)
  }

  const handleSendMessage = message => {
    console.log('Sending message:', message)
  }

  useEffect(() => {
    if (collection) {
      setLocalCollectionData(collection)
    }
  }, [collection])

  useEffect(() => {
    setIsLayoutReady(true)
    return () => setIsLayoutReady(false)
  }, [])

  useEffect(() => {
    addGlobalStyles()
  }, [open, page, subPage])

  useEffect(() => {
    if (
      (pageUrl === 'mini-course' && courseSettings) ||
      (pageUrl !== 'mini-course' &&
        collectionSettings &&
        collectionGeneralSettings?.inviteEmailBody)
    ) {
      setIsDataLoaded(true)
    }
  }, [courseSettings, collectionSettings, collectionGeneralSettings, pageUrl])

  useEffect(() => {
    if (isDataLoaded || page !== 'landing-page') {
      setIsDataLoaded(true)
    }
  }, [
    isDataLoaded,
    landingPageData,
    courseCards,
    course,
    collection,
    open,
    page,
    subPage,
  ])

  // Existing functions
  const addGlobalStyles = () => {
    const existingStyle = document.getElementById('ck-balloon-panel-style')
    if (existingStyle) {
      existingStyle.remove()
    }

    const styleEl = document.createElement('style')
    styleEl.id = 'ck-balloon-panel-style'

    if (page === 'page' && subPage !== 'editor') {
      if (open) {
        styleEl.textContent = `
          .ck.ck-balloon-panel.ck-balloon-panel_visible {
            padding-left: 280px !important;
          }
        `
      } else {
        styleEl.textContent = `
          .ck.ck-balloon-panel.ck-balloon-panel_visible {
            padding-left: 56px !important;
          }
        `
      }
    } else {
      styleEl.textContent = `
        .ck.ck-balloon-panel.ck-balloon-panel_visible {
          padding-left: 0 !important;
        }
      `
    }

    document.head.appendChild(styleEl)
  }

  const handleEditorChange = (event, editor) => {
    document.querySelectorAll('.video-wrapper').forEach(video => {
      const nextElement = video.nextElementSibling
      if (
        nextElement &&
        nextElement.tagName === 'P' &&
        (!nextElement.textContent.trim() ||
          nextElement.innerHTML === '<br data-cke-filler="true">')
      ) {
        nextElement.remove()
      }
    })
    const currentData = editor
      .getData()
      .replace(/<p class="empty-dom">&nbsp;<\/p>/g, '')
      .trim()

    setPreviousData(currentData)
    const rootElement = editor.model.document.getRoot()

    if (currentData === '') {
      editor.model.change(writer => {
        writer.remove(writer.createRangeIn(rootElement))

        const editorHeader = writer.createElement('editorHeader')
        writer.insert(editorHeader, rootElement, 0)

        const dropAreaWide = writer.createElement('dropAreaWide')
        writer.insert(dropAreaWide, rootElement, 1)

        writer.setSelection(dropAreaWide, 'end')
      })
    }

    editor.model.change(writer => {
      const root = editor.model.document.getRoot()
      const dropAreaNarrows = root._children._nodes.filter(
        child => child.name === 'paragraph',
      )
      dropAreaNarrows.forEach(area => {
        writer.remove(area)
      })
    })
  }

  // Editor configuration with updated simpleBox config
  const editorConfig = {
    toolbar: {
      items: isReadOnly
        ? []
        : [
            'undo',
            'redo',
            'heading',
            '|',
            'bold',
            'italic',
            'alignment',
            'fontColor',
            'link',
            '|',
            'imageGalleryAdd',
            'customVideo',
            '|',
            {
              name: 'listGroup',
              label: 'Show more items',
              items: [
                'fontBackgroundColor',
                'underline',
                'strikethrough',
                'removeFormat',
                'bulletedList',
                'numberedList',
                'horizontalLine',
                'codeBlock',
              ],
            },
            '|',
            'customDropdown',
            'InteractionBuilder',
          ],
      shouldNotGroupWhenFull: true,
    },
    indentBlock: {
      classes: [
        'custom-block-indent-a',
        'custom-block-indent-b',
        'custom-block-indent-c',
      ],
    },
    extraPlugins: [
      ImageGalleryPlugin,
      VideoGalleryPlugin,
      CustomDropdown,
      ButtonOptionsPlugin,
      TextToSpeechPlugin,
      CommunityBlockPlugin,
      PixelQuestPlugin,
      CrosswordPuzzlePlugin,
      RiddlePlugin,
      WordlePlugin,
      JumblePlugin,
      WordSearchPlugin,
      ScormPlugin,
    ],
    scormDispatch: dispatch,
    scormGetCardId: () => cardIdRef.current,
    imageGalleryPlugin: {
      dispatch: dispatch,
    },
    videoGalleryPlugin: {
      dispatch: dispatch,
      shopClips: shopClips,
    },
    buttonOptionsPlugin: {
      dispatch: dispatch,
    },
    removePlugins: ['ImageCaption'],
    mediaEmbed: {
      previewsInData: true,
    },
    autoParagraph: false,
    autosave:
      page !== 'landing-page'
        ? {
            waitingTime: 1000,
            save: async editor => {
              const currentData = normalizeEditorData(editor.getData())
              try {
                if (
                  currentData !== previousDataRef.current.trim() &&
                  saveData &&
                  currentData !== ''
                ) {
                  await saveData(currentData)
                  previousDataRef.current = currentData
                }
                return Promise.resolve()
              } catch (error) {
                console.error('Error saving editor data:', error)
                return Promise.reject(error)
              }
            },
          }
        : null,
    ui: {
      viewportOffset: {
        top: 70,
      },
    },

    plugins: [
      Autosave,
      AccessibilityHelp,
      Autoformat,
      AutoImage,
      Autosave,
      BlockQuote,
      RemoveFormat,
      Bold,
      Font,
      Link,
      Essentials,
      CodeBlock,
      Heading,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsert,
      ImageInsertViaUrl,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      ImageUpload,
      Indent,
      IndentBlock,
      Italic,
      List,
      ListProperties,
      HorizontalLine,
      CustomVideo,
      PageBreak,
      Paragraph,
      PictureEditing,
      SelectAll,
      Table,
      TableCaption,
      Strikethrough,
      Subscript,
      Superscript,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      TodoList,
      GeneralHtmlSupport,
      Underline,
      Undo,
      SimpleBox,
      Alignment,
      HtmlEmbed,
      SimpleBoxToolbar,
      InteractionBuilder,
      TextToSpeechPlugin,
      CommunityBlockPlugin,
      PixelQuestPlugin,
      CrosswordPuzzlePlugin,
      RiddlePlugin,
      JumblePlugin,
      ScormPlugin,
      CustomDropMarkerPositioning,
    ],
    htmlEmbed: {
      showPreviews: true,
      sanitizeHtml: inputHtml => {
        return {
          html: inputHtml,
          hasChanged: false,
        }
      },
    },
    link: {
      addTargetToExternalLinks: false,
      defaultProtocol: 'https://',
      decorators: {
        openInNewTab: {
          mode: 'manual',
          label: 'Open in new tab',
          defaultValue: true,
          attributes: {
            target: '_blank',
            rel: 'nofollow noreferrer noopener',
          },
        },
      },
    },
    language: language,
    fontData: fontFamily,
    htmlSupport: {
      allow: [
        {
          name: 'pre',
          attributes: true,
          classes: true,
        },
        {
          name: 'p',
          attributes: true,
          classes: true,
        },
        {
          name: 'ul',
          attributes: true,
          classes: true,
        },
        {
          name: 'ol',
          attributes: true,
          classes: true,
        },
        {
          name: 'span',
          attributes: true,
          classes: true,
        },
        {
          name: 'h1',
          attributes: true,
          classes: true,
        },
        {
          name: 'h2',
          attributes: true,
          classes: true,
        },
        {
          name: 'h3',
          attributes: true,
          classes: true,
        },
        {
          name: 'h4',
          attributes: true,
          classes: true,
        },
        {
          name: 'a',
          attributes: true,
          classes: true,
        },
        {
          name: 'code',
          attributes: true,
          classes: true,
        },
      ],
    },
    heading: {
      options: [
        {
          model: 'paragraph',
          title: 'Paragraph',
          class: 'ck-heading_paragraph',
        },
        {
          model: 'heading1',
          view: 'h1',
          title: 'Heading 1',
          class: 'ck-heading_heading1',
        },
        {
          model: 'heading2',
          view: 'h2',
          title: 'Heading 2',
          class: 'ck-heading_heading2',
        },
        {
          model: 'heading3',
          view: 'h3',
          title: 'Heading 3',
          class: 'ck-heading_heading3',
        },
      ],
    },
    simpleBox: {
      landingPageData: landingPageData || {},
      theme: theme,
      subPage: subPage,
      page: page,
      pageUrl: pageUrl,
      courseCards: courseCards || [],
      course: course || {},
      collection: headerCollectionOf(
        localCollectionData?.collection || {},
        landingPageData,
      ),
      collectionCards: localCollectionData?.courses || [],
      collectionList: collectionList || [],
      collectionSettings: collectionSettings || {},
      collectionGeneralSettings: collectionGeneralSettings || {},
      certificatePayment: certificatePayment,
      onAddButtonClick: position => {
        setIsOpen(true)
      },
      onEditInteraction: data => {
        handleOpenChatModal(data)
      },
      // Capture collection changes from editor
      onCollectionsChange: collections => {
        setEditorCollections(collections)
      },
    },
    image: {
      toolbar: ['imageTextAlternative', 'imageGalleryAi', 'imageGallery'],
    },
    video: {
      toolbar: [
        'aspectRatio',
        'videoGalleryLink',
        'videoGalleryChange',
        'videoSubtitles',
        'videoCustomize',
      ],
    },
    initialData: initialData
      ? initialData
      : `  
              <div class="simple-box-wrapper">
                <div class="simple-box">
                 <div class="column">
                     <p>I'm a paragraph. Click to start writing that will inform, inspire, or encourage your community. We know you have so many things to share with the world. Happy editing!</p>
                 </div>
               </div>
              </div>  
        `,
    enterMode: InlineEditor.ENTER_BR,
    licenseKey: ENV.LICENSE_KEY,
    list: {
      properties: {
        styles: true,
        startIndex: true,
        reversed: true,
      },
    },
    mention: {
      feeds: [
        {
          marker: '@',
          feed: [],
        },
      ],
    },
    placeholder: 'Type or paste your content here!',
    presenceList: {
      container: editorPresenceRef.current,
    },
    revisionHistory: {
      editorContainer: editorContainerRef.current,
      viewerContainer: editorRevisionHistoryRef.current,
      viewerEditorElement: editorRevisionHistoryEditorRef.current,
      viewerSidebarContainer: editorRevisionHistorySidebarRef.current,
      resumeUnsavedRevision: true,
    },
    sidebar: {
      container: editorAnnotationsRef.current,
    },
    table: {
      contentToolbar: [
        'tableColumn',
        'tableRow',
        'mergeTableCells',
        'tableProperties',
        'tableCellProperties',
      ],
    },
  }

  // Existing useEffect for doubleClickData
  useEffect(() => {
    if (doubleClickData && editorRef.current) {
      const { data, type } = doubleClickData
      const editor = editorRef.current

      replacedropAreaWideContent(
        editor,
        data,
        type,
        dispatch,
        Embed,
        CommunityBoardModal,
        fontFamily,
        language,
        languageSettings?.value?.selected || language || 'en',
      )
      setDoubleClickData(null)
    }
  }, [doubleClickData])

  if (pageUrl.includes('landing-page') && !isDataLoaded) {
    return null
  }
  if (!isDataLoaded) {
    return null
  }
  const landingPageSection = landingPageData?.sections?.find(
    x => x.type === editorEnum.headerType,
  )

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css?family=${fontFamily}:wght@100;200;300;400;500;600;700;800;900&display=swap`}
        />
      </Helmet>

      <div className="main-container">
        <div className="presence" ref={editorPresenceRef}></div>
        <div
          className="editor-container editor-container_inline-editor editor-container_include-annotations editor-container_include-block-toolbar"
          ref={editorContainerRef}
          // Course list, catalog and continue learning cards draw their progress rows in the
          // page's primary colour. It is set here, from the store, rather than on each block when
          // it renders, so a colour changed in the landing page settings reaches them right away.
          style={pageAccent ? { '--page-accent': pageAccent } : undefined}
        >
          <div className="editor-container__editor-wrapper">
            <div className="editor-container__editor">
              {isLayoutReady && editorConfig && (
                <div className={classes.ckEditorFont}>
                  <CKEditor
                    key={`editor-${landingPageSection?.content?.type || 1}`}
                    fontSize={fontSize}
                    fontFamily={fontFamily}
                    theme={theme}
                    page={page}
                    editor={InlineEditor}
                    onChange={!isReadOnly ? handleEditorChange : undefined}
                    onReady={editor => {
                      // Seed the autosave baseline from what CKEditor actually produced, not from
                      // the HTML we handed it. Seeded with initialData, the first autosave after
                      // opening a page always saw a difference — the plugins' own normalisation,
                      // which for simple-box means a dozen default attributes and a <p> wrapper —
                      // and saved it. That was invisible while it only rewrote the stored text;
                      // now the same write creates a working copy, so merely opening a page marked
                      // it as having unpublished changes.
                      previousDataRef.current = normalizeEditorData(
                        editor.getData(),
                      )

                      const editorWrapper = document.querySelector(
                        '.editor-container__editor',
                      )
                      const editorPointer = document.querySelector(
                        '.editor-container__editor-wrapper',
                      )
                      editor.model.document.selection.on('change:range', () => {
                        const selectedElement = editor.model.document.selection.getSelectedElement()
                        const toolbar = editor.ui.view.toolbar
                        const header = document.getElementsByClassName(
                          'editor-header',
                        )[0]
                        if (header) {
                          header.addEventListener('click', () => {
                            if (toolbar) {
                              toolbar.isEnabled = false

                              if (toolbar.element) {
                                toolbar.element.style.display = 'none'
                              }
                              handleHeaderSettingsModal(dispatch, pageUrl)
                            }
                          })
                        }
                      })

                      editor.model.change(writer => {
                        const root = editor.model.document.getRoot()
                        const hasHeader = Array.from(root.getChildren()).some(
                          child => child.name === 'editorHeader',
                        )
                        if (!hasHeader) {
                          const editorHeader = writer.createElement(
                            'editorHeader',
                          )
                          writer.insert(editorHeader, root, 0)
                        }
                      })
                      if (isReadOnly) {
                        editorPointer.style.pointerEvents = 'none'
                        editorWrapper.style.pointerEvents = 'none'
                      }

                      // Disable link command for basic/trial users
                      if (isLinkRestrictedUser) {
                        if (editor.commands.get('link')) {
                          editor.commands
                            .get('link')
                            .forceDisabled('linkRestrictedUser')
                        }
                        stripPastedLinks(editor)
                      }

                      handleEditorReady(
                        editorRef,
                        onDropClose,
                        dispatch,
                        Embed,
                        CommunityBoardModal,
                      )(editor)
                      // Debug: Check if decorators are initialized
                      const linkCommand = editor.commands.get('link')
                      console.log('Link command:', linkCommand)
                      console.log(
                        'Manual decorators:',
                        linkCommand?.manualDecorators,
                      )
                      console.log(
                        'Manual decorators length:',
                        linkCommand?.manualDecorators?.length,
                      )
                      if (linkCommand?.manualDecorators) {
                        for (const decorator of linkCommand.manualDecorators) {
                          console.log(
                            'Decorator:',
                            decorator.id,
                            decorator.label,
                          )
                          setTimeout(() => {
                            const linkUI = editor.plugins.get('LinkUI')
                            console.log('LinkUI plugin:', linkUI)
                            console.log('LinkUI formView:', linkUI?.formView)
                            console.log(
                              'FormView manualDecoratorSwitches:',
                              linkUI?.formView?._manualDecoratorSwitches,
                            )
                            console.log(
                              'FormView manualDecoratorSwitches length:',
                              linkUI?.formView?._manualDecoratorSwitches
                                ?.length,
                            )
                          }, 1000)
                        }
                      }
                    }}
                    config={editorConfig}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ChatInterface
        isModalOpen={isChatModalOpen}
        handleCloseModal={handleCloseChatModal}
        onStreamResponse={handleStreamResponse}
        onSendMessage={handleSendMessage}
        position={position}
        initialLoading={isInitialLoading}
        onInteractionUsed={() => {
          handleCloseChatModal()
        }}
        selectedElementData={chatModalData}
        mode={chatModalData?.mode || 'normal'}
      />
    </>
  )
}

export default withStyles(styles)(CKEditorComponent)
